import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const PRODUCT_RE = /\/products\/([^/?#]+)/;
  const dataCache = new Map();
  const MAX_BUNDLED_CART_SECTIONS = 5;

  const getCartSectionIds = () => {
    const sectionIds = new Set();

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }

      if (sectionIds.size >= MAX_BUNDLED_CART_SECTIONS) break;
    }

    return Array.from(sectionIds);
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');

  const money = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value); }
    catch (_) { return `$${value.toFixed(2)}`; }
  };

  const getHandle = (raw) => {
    try {
      const base = new URL(document.baseURI);
      const url = new URL(raw, base);
      if (url.origin !== base.origin) return '';
      const match = url.pathname.match(PRODUCT_RE);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) { return ''; }
  };

  const getData = (handle) => {
    if (!handle) return null;
    if (dataCache.has(handle)) return dataCache.get(handle);
    const node = document.querySelector(`script[data-gd-qv-product="${CSS.escape(handle)}"]`);
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || '{}');
      if (!data?.variants?.length) return null;
      dataCache.set(handle, data);
      return data;
    } catch (error) {
      console.warn('[Good Deeds Quick View] Invalid product data', error);
      return null;
    }
  };

  const ensureDialog = () => {
    let dialog = document.getElementById('gd-product-quick-view');
    if (dialog instanceof HTMLDialogElement) return dialog;
    dialog = document.createElement('dialog');
    dialog.id = 'gd-product-quick-view';
    dialog.setAttribute('aria-label', 'Product quick view');
    dialog.innerHTML = `<div class="gd-qv-shell"><button class="gd-qv-close" type="button" aria-label="Close">×</button><div class="gd-qv-content"></div></div>`;
    dialog.querySelector('.gd-qv-close')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => document.documentElement.classList.remove('gd-qv-open'));
    document.body.appendChild(dialog);
    return dialog;
  };

  const selectedVariant = (data, id) => data.variants.find((v) => String(v.id) === String(id)) || data.variants.find((v) => v.available) || data.variants[0];

  const render = (data) => {
    const dialog = ensureDialog();
    const content = dialog.querySelector('.gd-qv-content');
    const initial = selectedVariant(data);
    const initialImage = initial?.image || data.featured_image || '';
    const options = data.variants.length > 1 ? `
      <label class="gd-qv-label" for="gd-qv-variant">Options</label>
      <select class="gd-qv-select" id="gd-qv-variant">
        ${data.variants.map((v) => `<option value="${escapeHtml(v.id)}" ${v.available ? '' : 'disabled'} ${String(v.id) === String(initial.id) ? 'selected' : ''}>${escapeHtml(v.title)}${v.available ? '' : ' — Sold out'}</option>`).join('')}
      </select>` : '';
    const rating = Number(data.review_count || 0) > 0
      ? `<div class="gd-qv-rating"><span aria-hidden="true">★★★★★</span><strong>${escapeHtml(data.review_rating || '')}</strong><span>(${escapeHtml(data.review_count)} reviews)</span></div>`
      : `<div class="gd-qv-rating"><span>No customer reviews yet.</span></div>`;
    const descriptionTemplate = document.querySelector(`template[data-gd-qv-description="${CSS.escape(data.handle)}"]`);
    const reviewsTemplate = document.querySelector(`template[data-gd-qv-reviews="${CSS.escape(data.handle)}"]`);
    const description = descriptionTemplate?.innerHTML?.trim() || '<p>No description has been added yet.</p>';
    const reviews = reviewsTemplate?.innerHTML?.trim() || '<p>No customer reviews yet.</p>';

    content.innerHTML = `
      <article class="gd-qv-product">
        <div class="gd-qv-media">${initialImage ? `<img src="${escapeHtml(initialImage)}" alt="${escapeHtml(data.title)}">` : '<span>No product image available.</span>'}</div>
        <div class="gd-qv-details">
          <p class="gd-qv-kicker">QUICK VIEW</p>
          <h2 class="gd-qv-title">${escapeHtml(data.title)}</h2>
          ${rating}
          <div class="gd-qv-price"></div>
          ${options}
          <button type="button" class="button button--full-width gd-qv-add">Add to cart</button>
          <p class="gd-qv-message" aria-live="polite"></p>
          <div class="gd-qv-description rte">${description}</div>
          <section class="gd-qv-reviews"><h3>Reviews</h3>${reviews}</section>
          <a class="gd-qv-full-link" data-gd-qv-full href="${escapeHtml(data.url)}">View full product page</a>
        </div>
      </article>`;

    const select = content.querySelector('.gd-qv-select');
    const price = content.querySelector('.gd-qv-price');
    const add = content.querySelector('.gd-qv-add');
    const message = content.querySelector('.gd-qv-message');
    const img = content.querySelector('.gd-qv-media img');

    const update = () => {
      const variant = selectedVariant(data, select?.value || initial.id);
      if (!variant) return;
      price.innerHTML = `${Number(variant.compare_at_price || 0) > Number(variant.price || 0) ? `<s>${money(variant.compare_at_price, data.currency)}</s>` : ''}<strong>${money(variant.price, data.currency)}</strong>`;
      add.disabled = !variant.available;
      add.textContent = variant.available ? 'Add to cart' : 'Sold out';
      if (img && (variant.image || data.featured_image)) img.src = variant.image || data.featured_image;
    };
    select?.addEventListener('change', update);
    update();

    add?.addEventListener('click', async () => {
      const variant = selectedVariant(data, select?.value || initial.id);
      if (!variant?.available) return;
      add.disabled = true;
      add.textContent = 'Adding…';
      message.textContent = '';
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      dialog.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferredEventPromise.promise,
      }));

      try {
        const root = window.Shopify?.routes?.root || '/';
        const sectionIds = getCartSectionIds();
        const requestBody = {
          items: [{ id: Number(variant.id), quantity: 1 }],
          ...(sectionIds.length > 0 ? { sections: sectionIds.join(',') } : {}),
          sections_url: window.location.pathname,
        };
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item to cart.');

        const cartResponse = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('The item was added, but the cart could not be refreshed.');
        const cart = await cartResponse.json();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'good-deeds-product-quick-view',
            sourceId: dialog.id,
            itemCount: 1,
            productId: String(data.id || ''),
            sections: payload.sections,
            didError: false,
          },
        });

        add.textContent = 'Added ✓';
        message.textContent = 'Added to cart.';
        dialog.close();
      } catch (error) {
        deferredEventPromise.reject(error);
        add.disabled = false;
        add.textContent = 'Add to cart';
        message.textContent = error?.message || 'Could not add this item to cart.';
      }
    });

    try { if (window.jdgm?.widget_load) window.jdgm.widget_load(); } catch (_) {}
    document.documentElement.classList.add('gd-qv-open');
    if (!dialog.open) dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;
    if (event.target.closest('button, input, select, textarea, label, #gd-product-quick-view')) return;
    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const handle = getHandle(anchor.href);
    if (!handle) return;
    const data = getData(handle);
    if (!data) return; // Important: fall back to the normal full product page.
    event.preventDefault();
    event.stopImmediatePropagation();
    render(data);
  }, true);
})();
